import React, { useState, useEffect } from "react";

import api from "../../../services/api";

import { Content, CardsContainer } from "./styles";
import { i18n } from "../../../localization/i18n";

import { useNavigate, useParams } from "react-router-dom";
import { NewProductBox } from "../../../components/NewProductBox";
import { SolutionModal } from "../../../components/SolutionModal";
import { useAuth } from "../../../hooks/AuthContext";

import { showToast } from "../../../components/CustomToast";
import { useOnboard } from "../../../hooks/OnboardContext";
import { countries, orderTypes, roles } from "../../../utils/constants";
import { orderTypeOptions } from "../mocks";
import { usePrinters } from "../../../hooks/PrintersContext";
import { IPrice } from "../dtos";
import { StandardOrderConfirmationModal } from "../components/StandardOrderConfirmationModal";
import { FixcoinPaymentDetails } from "../../../components/FixcoinPaymentDetails";
export interface IPrinter {
  id: string;
  name: string;
}

export interface ISolution {
  id: string;
  name: string;
  sides: string[];
  name_key: string;
  category_key: string;
  field_key: string;
  form_type?: string;
  link_img: string;
  download_link: string;
  category: string;
  link_guide: string;
  number_measures: number;
  sample: ISample;
  solution_category: ICategory;
  is_custom: boolean;
  is_standard: boolean;
  is_download: boolean;
  standards: Array<any>;
}

export interface SelectedSolution {
  id: string;
  name: string;
  solution_category: any;
  application_video_link?: string;
  background_image_link?: string;
  link_guide?: string;
  description?: string;
}

export interface ISample {
  id?: string;
  solution_id?: string;
  printer_id?: string;
  link?: string;
}

export interface ICategory {
  id: string;
  name: string;
  category_key: string;
  field_key: string;
}

export interface IStandardFileChoice {
  standard: {
    id: string,
    size: string,
  }
  side: string;
  printer_id: string;
}

export default function Solutions() {
  const { setNavigationAction, user, currency, licenseeFixcoins, reloadUser, isFixiter, canPlaceOrders } = useAuth();
  const { licensee } = useOnboard();
  const navigate = useNavigate();
  const { userHasPrinter, defaultPrinter } = usePrinters();
  const [filePrice, setFilePrice] = useState<IPrice>();

  const [solutions, setSolutions] = useState<ISolution[]>([]);
  const [selectedSolution, setSelectedSolution] = useState<SelectedSolution>();
  const [solutionModal, setSolutionModal] = useState(false);
  const [ordersCount, setOrdersCount] = useState(false);
  const [selectedSolutionID, setSelectedSolutionID] = useState("")
  const [deliveryAddress, setDeliveryAddress] = useState("")
  const [isFixcoinModalOpen, setIsFixcoinModalOpen] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [planSolutions, setPlanSolutions] = useState<ISolution[]>([]); // Aqui é onde eu vou setar o array de ID que vier do back
  const [standardFileChoiceData, setStandardFileChoiceData] = useState<IStandardFileChoice>({
    printer_id: defaultPrinter && defaultPrinter?.value
  } as IStandardFileChoice)

  let { category_key } = useParams<keyof ICategory>();

  async function getAvailableSolutionsFromSignature() {
    try {
      await api
        .get(`/signatures/solutions?category_key=${category_key}`)
        .then((response) => {
          setPlanSolutions(response.data);
        });
    } catch (error) {
      showToast({
        type: "error",
        message: i18n.t("financial.error.get_signature"),
      });
    }
  }

  async function getSolutions() {
    try {
      api.get(`/categories/key/${category_key}`).then((response) => {
        setSolutions(response.data.solutions);
      });
    } catch (error) {
      showToast({
        type: "error",
        message: "Não foi possivel carregar as soluções",
      });
    }
  }

  async function handleSubmit() {
    if (isSubmitting) return;

    const isFixiterOrder = isFixiter || !userHasPrinter;

    if (isFixiterOrder && !licensee?.provider_id) {
      showToast({
        type: "error",
        message: "Este Fixiter não possui um Provedor de Impressão configurado.",
      });
      return;
    }

    if (!standardFileChoiceData.standard) {
      showToast({
        type: "error",
        message: "Por favor, selecione o tamanho",
      });
      return;
    }

    if (!standardFileChoiceData.side) {
      showToast({
        type: "error",
        message: "Por favor, selecione o lado",
      });
      return;
    }

    if (!isFixiterOrder && userHasPrinter && !standardFileChoiceData.printer_id) {
      showToast({
        type: "error",
        message: "Por favor, selecione a impressora",
      });
      return;
    }

    if (!licensee) {
      return;
    }

    const currentSolutionId = selectedSolutionID;
    setIsSubmitting(true);
    setSelectedSolutionID("");

    const abortController = new AbortController();
    const signal = abortController.signal;

    try {
      const { data } = await api.get(`/prices/calc/${currentSolutionId}`, {
        signal,
      });

      const body = {
        provider: isFixiterOrder ? licensee.provider_id : null,
        solution_id: currentSolutionId,
        category:
          isFixiterOrder
            ? orderTypes.fixiter
            : orderTypeOptions.find((option) => option.value === 'is_patient')
                ?.value || orderTypes.patient,
        printer_id:
          !isFixiterOrder && user.role === roles.licensee
            ? defaultPrinter?.value
            : undefined,
        total: Number(data.file_price),
        solution_type: "standard",
        licensee_id: licensee.id,
        quantity: 1,
        delivery_address: deliveryAddress,
      };

      let orderId = createdOrderId;
      let orderWarning: string | undefined = undefined;

      if (!orderId) {
        const orderResponse = await api.post("/orders", body, { signal });
        orderId = orderResponse.data.id;
        orderWarning = orderResponse.data?.warning;
        setCreatedOrderId(orderId);
      }

      await api.post(
        "/file-choice",
        {
          order_id: orderId,
          printer_id: standardFileChoiceData.printer_id,
          side: standardFileChoiceData.side,
          solution_standard_id: standardFileChoiceData.standard.id,
        },
        { signal },
      );

      setCreatedOrderId(undefined);

      if (orderWarning) {
        showToast({
          type: "warn",
          message: `Pedido gerado com sucesso! Atenção: ${orderWarning}`,
        });
      } else {
        showToast({
          type: "success",
          message: i18n.t("customSolution.successToast"),
        });
      }

      navigate("/orders");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.other ||
        (error.response?.data?.message
          ? i18n.t(error.response.data.message)
          : "Não foi possível salvar pedido");

      showToast({
        type: "error",
        message: errorMessage,
      });

      if (
        errorMessage.toLowerCase().includes("fixcoin") ||
        errorMessage.toLowerCase().includes("saldo")
      ) {
        setIsFixcoinModalOpen(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function getUserOrdersCount() {
    const date = new Date();
    const queryList = [];
    queryList.push(`month=${date.getMonth() + 1}`);
    queryList.push(`year=${date.getFullYear()}`);

    try {
      await api
        .get(`/orders/user/verify?${queryList.join("&")}`)
        .then((response) => {
          setOrdersCount(response.data);
        });
    } catch (error) {
      return;
    } finally {
      if (ordersCount) {
        showToast({
          type: "error",
          message: "Você atingiu seu limite de pedidos por mês, contate o SAF!",
        });
      }
    }
  }

  function changeOrderData(data: any) {
    setDeliveryAddress(data.delivery_address)
  }


  useEffect(() => {
    if (category_key && solutions?.length) {
      getAvailableSolutionsFromSignature();
    }
  }, [category_key, solutions]);

  useEffect(() => {
    if (category_key && licensee) {
      getSolutions();
    }
  }, [category_key, licensee]);

  useEffect(() => {
    setNavigationAction({
      action: "back",
      hasButton: true,
      description: i18n.t("solutions.solutions.header"),
    });
  }, [setNavigationAction]);

  return (
    <>
      <FixcoinPaymentDetails 
        isOpen={isFixcoinModalOpen}
        onHandleCancel={() => setIsFixcoinModalOpen(false)}
        currency={currency}
        licenseeFixcoins={licenseeFixcoins}
        onSuccess={() => reloadUser()}
      />
      <Content>
        {selectedSolution && (
          <SolutionModal
            open={solutionModal}
            closeModal={() => setSolutionModal(false)}
            solution={selectedSolution}
            licenseeCountry={
              licensee?.country &&
                countries[licensee.country as keyof typeof countries]
                ? countries[licensee.country as keyof typeof countries]
                : countries.default
            }
          />
        )}

        <CardsContainer>
          {solutions?.map((solution) => {
            const availableSolution = planSolutions?.find(
              (planSolution) => planSolution.id === solution.id
            );
            return (
              <>
                {solution.id === selectedSolutionID &&
                  <StandardOrderConfirmationModal
                    open={solution.id === selectedSolutionID}
                    solution={solution}
                    standardFileChoice={standardFileChoiceData}
                    setStandardFileChoice={setStandardFileChoiceData}
                    handleCloseModal={() => {
                      setSelectedSolutionID("")
                      setCreatedOrderId(undefined)
                      setStandardFileChoiceData({ printer_id: defaultPrinter?.value } as IStandardFileChoice)
                    }}
                    changeOrder={changeOrderData}
                    onConfirmOrder={handleSubmit}
                    currency={currency}
                    loading={isSubmitting} />
                }

                <NewProductBox
                  licenseeCountry={
                    licensee?.country &&
                      countries[licensee.country as keyof typeof countries]
                      ? countries[licensee.country as keyof typeof countries]
                      : countries.default
                  }
                  solutionId={solution.id}
                  link_img={solution.link_img}
                  solutionName={solution.name}
                  solutionCategory={solution.category}
                  solutionKey={solution.name_key}
                  solutionForm={solution.form_type}
                  is_custom={solution.is_custom}
                  is_standard={solution.is_standard}
                  is_download={solution.is_download}
                  download_link={solution.download_link}
                  standards={solution.standards}
                  isAvailable={
                    (!planSolutions || !!availableSolution) &&
                    !licensee?.is_blocked &&
                    (licensee?.situation === 'active' || licensee?.situation === 'operational')
                  }
                  ordersCount={true}
                  handleCreateStandardOrder={() => {
                    setSelectedSolutionID(solution.id)
                  }}
                  onInfoClick={() => {
                    setSelectedSolution({
                      id: solution.id,
                      name: solution.name,
                      solution_category: solution.solution_category,
                      link_guide: solution.link_guide,
                    });
                    setSolutionModal(true);
                  }}
                />
              </>
            );
          })}
        </CardsContainer>
      </Content>
    </>
  );
}
